import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Upload, Check, RotateCcw, ChevronRight, Clock, Film, ExternalLink, Plus, Send, Building2, Calendar, X, Pencil } from "lucide-react";
import UserAutocomplete from "../components/UserAutocomplete";
import Hint from "../components/Hint";
import { isSuperAdmin } from "../hooks/useSuperAdmin";
import { KPI_FIELDS, KPI_GROUPS, kpiDisplay, kpiToInput, kpiToStore } from "../lib/kpiFields";

const ASSET_TYPE_LABELS = { reel: "Reel", ad_film: "Ad Film", static: "Static", carousel: "Carousel", story: "Story", thumbnail: "Thumbnail" };
// Mirrors PRODUCTION_ROLES in backend/server.py — keep the two in step.
const TEAM_ROLES = ["producer", "creative_lead", "account_manager", "writer", "director", "dop", "line_producer", "editor", "colorist", "sound_mixer", "motion_designer", "designer", "hmu", "stylist", "social_media_manager"];
// Shoot day = four 3-hour slots, 9am–9pm; a shoot books 1–4 contiguous slots.
const SLOT_DEFS = [
  { k: 0, s: 9, e: 12, label: "9–12" },
  { k: 1, s: 12, e: 15, label: "12–3" },
  { k: 2, s: 15, e: 18, label: "3–6" },
  { k: 3, s: 18, e: 21, label: "6–9" },
];
const PHASE_COLORS = { pre: "#64748B", shoot: "#F59E0B", post: "#3B82F6", review: "#A855F7", done: "#22C55E" };
const STATUS_PHASE = {
  "Concept": "pre", "Script": "pre", "Treatment": "pre", "Design Brief": "pre", "Brief": "pre",
  "Pre-Production": "pre", "Shoot Scheduled": "shoot", "Shooting": "shoot", "Design/Shoot": "shoot",
  "Edit": "post", "Color Grade": "post", "Sound Mix": "post", "Design V1": "post",
  "Internal Review": "review", "Client Review": "review", "Revisions": "review", "Review": "review",
  "Approved": "done", "Live": "done",
};

export default function AssetDetailPage() {
  const { assetId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newVersionUrl, setNewVersionUrl] = useState("");
  const [newVersionNotes, setNewVersionNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [editingTeam, setEditingTeam] = useState(false);
  const [teamEdits, setTeamEdits] = useState({});
  const [showShootBooking, setShowShootBooking] = useState(false);
  const [studios, setStudios] = useState([]);
  const [shootForm, setShootForm] = useState({ studio_id: "", date: "", slots: [0, 1, 2, 3] });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  // Live URL capture: prompted when advancing to Live without a URL on file.
  const [showLiveUrlModal, setShowLiveUrlModal] = useState(false);
  const [liveUrlInput, setLiveUrlInput] = useState("");
  // Inline-edit state for the Files-panel Live URL row (separate from the
  // modal flow so editing an existing URL is a one-click affordance).
  const [editingLiveUrl, setEditingLiveUrl] = useState(false);
  const [liveUrlEditInput, setLiveUrlEditInput] = useState("");
  // Inline title edit — admins/team can rename the asset post-create.
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  // Inline edits for file links (brief_link, script_link, treatment_link,
  // frame_io_link, ...). Bug d6590983af5b: title was the ONLY editable field
  // post-create. Now any link key can be updated via click-to-edit; saving
  // PUTs the merged files object so file_log keeps proper attribution.
  const [editingFileKey, setEditingFileKey] = useState(null);
  const [fileEditInput, setFileEditInput] = useState("");
  // Inline platform edit — same fix; users were stuck with whatever they
  // picked at create time even though backend PUT happily accepts it.
  const [editingPlatform, setEditingPlatform] = useState(false);
  const [platformDraft, setPlatformDraft] = useState("");
  // Post-Live rubric + engagement state
  const [rubricStatus, setRubricStatus] = useState(null);
  const [engagementForm, setEngagementForm] = useState({ shares: "", saves: "", er_rate: "" });
  const [engagementEdit, setEngagementEdit] = useState(false);
  const [kpiEditing, setKpiEditing] = useState(false);
  const [kpiForm, setKpiForm] = useState({});
  const [perfOpen, setPerfOpen] = useState(null);   // null = auto (open if data/live)
  const [scoreInputs, setScoreInputs] = useState({});
  const [scoreNotes, setScoreNotes] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);
  // Hero thumbnail state (Phase 1 Production rebuild) — declared up here
  // BEFORE any early returns so React's Rules of Hooks stay satisfied.
  const [heroImgErrored, setHeroImgErrored] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [heroBust, setHeroBust] = useState(0); // bumps to force <img> re-render after regenerate

  useEffect(() => { loadAsset(); }, [assetId]);

  const loadAsset = async () => {
    try {
      const res = await api.get(`/production/assets/${assetId}`);
      setAsset(res.data);
      setTeamEdits(res.data.team || {});
      // Hydrate engagement form fields from saved metrics so the inputs
      // start pre-filled when the user clicks edit.
      const em = res.data.engagement_metrics;
      if (em) {
        setEngagementForm({
          shares: em.shares ?? "",
          saves: em.saves ?? "",
          er_rate: em.er_rate ?? "",
        });
      }
      // Lazy-load rubric status only when the asset is past Approved —
      // saves a round-trip on every Concept-stage asset detail view.
      if (["Live", "Approved"].includes(res.data.status)) {
        try {
          const r = await api.get(`/production/assets/${assetId}/rubric-status`);
          setRubricStatus(r.data);
        } catch { /* non-blocking */ }
      } else {
        setRubricStatus(null);
      }
    } catch { toast.error("Asset not found"); navigate("/production"); }
    finally { setLoading(false); }
  };

  const openKpiEdit = () => {
    const form = {};
    KPI_FIELDS.forEach((f) => { form[f.key] = kpiToInput(f, (asset?.engagement_metrics || {})[f.key]); });
    setKpiForm(form);
    setKpiEditing(true);
  };
  const saveKpis = async () => {
    const em = { ...(asset?.engagement_metrics || {}) };
    KPI_FIELDS.forEach((f) => {
      const v = kpiToStore(f, kpiForm[f.key]);
      if (v === undefined) delete em[f.key]; else em[f.key] = v;
    });
    try {
      await api.put(`/production/assets/${assetId}`, { engagement_metrics: em });
      toast.success("KPIs saved");
      setKpiEditing(false);
      loadAsset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save KPIs");
    }
  };

  const saveEngagement = async () => {
    try {
      await api.post(`/production/assets/${assetId}/engagement`, {
        shares: engagementForm.shares === "" ? null : engagementForm.shares,
        saves: engagementForm.saves === "" ? null : engagementForm.saves,
        er_rate: engagementForm.er_rate === "" ? null : engagementForm.er_rate,
      });
      toast.success("Engagement saved");
      setEngagementEdit(false);
      loadAsset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save engagement");
    }
  };

  const submitRubricRating = async () => {
    if (!rubricStatus) return;
    const missing = (rubricStatus.rubric_keys || []).filter((k) => !scoreInputs[k]);
    if (missing.length) {
      toast.error(`Pick a score for: ${missing.join(", ")}`);
      return;
    }
    setSubmittingRating(true);
    try {
      await api.post(`/production/assets/${assetId}/rubric-rating`, {
        scores: scoreInputs,
        notes: scoreNotes,
      });
      toast.success("Rating submitted");
      setScoreInputs({});
      setScoreNotes("");
      loadAsset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmittingRating(false);
    }
  };

  const updateStatus = async (newStatus, extraPayload = {}) => {
    // Intercept: if advancing TO "Shoot Scheduled", prompt for studio booking
    if (newStatus === "Shoot Scheduled") {
      try {
        const res = await api.get("/production/studios");
        setStudios(res.data || []);
      } catch { setStudios([]); }
      const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
      setShootForm({ studio_id: "", date: tomorrow, shift: "FULL_DAY" });
      setShowShootBooking(true);
      return;
    }
    // Intercept: moving to Live requires a live_url. If we don't have one
    // already on the asset, open the live-URL modal and stop here — the
    // modal's submit will recurse with extraPayload set.
    if (newStatus === "Live" && !asset?.live_url && !extraPayload.live_url) {
      setShowLiveUrlModal(true);
      return;
    }
    try {
      await api.put(`/production/assets/${assetId}`, { status: newStatus, ...extraPayload });
      toast.success(`Status → ${newStatus}`);
      loadAsset();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const confirmShootBooking = async () => {
    if (!shootForm.date) { toast.error("Pick a shoot date"); return; }
    setBookingSubmitting(true);
    try {
      // 1. Advance asset to "Shoot Scheduled"
      await api.put(`/production/assets/${assetId}`, { status: "Shoot Scheduled" });
      // 2. Create the shoot record — booked slots → start/end (min..max = contiguous)
      const sel = [...(shootForm.slots || [])].sort((a, b) => a - b);
      if (!sel.length) { toast.error("Pick at least one slot"); setBookingSubmitting(false); return; }
      const startH = SLOT_DEFS[sel[0]].s, endH = SLOT_DEFS[sel[sel.length - 1]].e;
      const shootPayload = {
        production_id: asset.production_id || asset.asset_id,
        title: asset.title,
        venue_type: shootForm.studio_id ? "studio" : "outdoor",
        studio_id: shootForm.studio_id || undefined,
        start_at: `${shootForm.date}T${String(startH).padStart(2, "0")}:00:00`,
        end_at: `${shootForm.date}T${String(endH).padStart(2, "0")}:00:00`,
      };
      await api.post("/shoots", shootPayload);
      toast.success("Shoot scheduled!");
      setShowShootBooking(false);
      loadAsset();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to schedule shoot"); }
    finally { setBookingSubmitting(false); }
  };

  const uploadVersion = async () => {
    if (!newVersionUrl.trim()) { toast.error("URL required"); return; }
    try {
      await api.post(`/production/assets/${assetId}/versions`, { url: newVersionUrl, notes: newVersionNotes });
      toast.success("Version uploaded");
      setNewVersionUrl(""); setNewVersionNotes("");
      loadAsset();
    } catch { toast.error("Failed"); }
  };

  const submitReview = async (decision) => {
    // Quality rating used to be required at approve time. Decoupled in
    // Phase 2 — the multi-dimensional rubric now lives in a separate flow
    // that fires 7 days post-Live (see /rubric-rating). Approve is open to
    // anyone on the team and just needs an optional note.
    try {
      const latestVersion = (asset.versions || []).length;
      const payload = {
        decision,
        notes: reviewNotes,
        version_number: latestVersion,
      };
      await api.post(`/production/assets/${assetId}/reviews`, payload);
      toast.success(decision === "approve" ? "Approved" : "Changes requested");
      setReviewNotes("");
      loadAsset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const saveTeam = async () => {
    try {
      // Send only the roles that actually changed. teamEdits is seeded with the
      // whole crew on load, so posting it wholesale re-asserts stale values and
      // would stomp a colleague's reassignment made while this panel was open.
      const base = asset.team || {};
      const changed = {};
      Object.keys({ ...base, ...teamEdits }).forEach((role) => {
        if ((teamEdits[role] || "") !== (base[role] || "")) changed[role] = teamEdits[role] || "";
      });
      if (Object.keys(changed).length === 0) { setEditingTeam(false); return; }
      await api.put(`/production/assets/${assetId}`, { team: changed });
      toast.success("Team updated");
      setEditingTeam(false);
      loadAsset();
    } catch { toast.error("Failed"); }
  };

  // Save inline file-link edit. Backend merges only the changed keys via the
  // files dict; file_log gets a proper attribution row when val is non-empty.
  const saveFileLink = async (key) => {
    const newVal = (fileEditInput || "").trim();
    try {
      const nextFiles = { ...(asset.files || {}), [key]: newVal };
      await api.put(`/production/assets/${assetId}`, { files: nextFiles });
      toast.success(newVal ? "Link updated" : "Link cleared");
      setEditingFileKey(null);
      setFileEditInput("");
      loadAsset();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const savePlatform = async () => {
    const v = (platformDraft || "").trim();
    try {
      await api.put(`/production/assets/${assetId}`, { platform: v });
      toast.success("Platform updated");
      setEditingPlatform(false);
      setPlatformDraft("");
      loadAsset();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-[var(--t2)] text-xl font-heading">LOADING...</div></div>;
  if (!asset) return null;

  const pipeline = asset.pipeline || [];
  const currentIdx = pipeline.indexOf(asset.status);
  const phase = STATUS_PHASE[asset.status] || "pre";
  const phaseColor = PHASE_COLORS[phase];
  const isReviewStatus = ["Internal Review", "Client Review", "Review"].includes(asset.status);
  const versions = asset.versions || [];
  const reviews = asset.reviews || [];

  // ── at-a-glance health: go-live countdown + risk flags + stage dates ──
  const fmtShort = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }); } catch { return ""; } };
  const dDiff = (a, b) => { if (!a || !b) return null; const n = Math.round((new Date(b) - new Date(a)) / 86400000); return Number.isNaN(n) ? null : n; };
  const isLive = asset.status === "Live" || !!asset.actual_go_live;
  const glTarget = asset.revised_go_live || asset.planned_go_live;
  const goLiveChip = (() => {
    if (isLive) return { label: "Live", color: "#16a34a", bg: "rgba(22,163,74,.14)" };
    if (!glTarget) return null;
    const days = Math.ceil((new Date(glTarget) - new Date()) / 86400000);
    if (days < 0) return { label: `Overdue ${-days}d`, color: "#dc2626", bg: "rgba(220,38,38,.14)" };
    if (days === 0) return { label: "Go-live today", color: "#d97706", bg: "rgba(217,119,6,.14)" };
    return { label: `Live in ${days}d`, color: days <= 3 ? "#d97706" : "#16a34a", bg: days <= 3 ? "rgba(217,119,6,.14)" : "rgba(22,163,74,.14)" };
  })();
  const risks = (() => {
    const r = [];
    if (!isLive && glTarget) { const od = -Math.ceil((new Date(glTarget) - new Date()) / 86400000); if (od > 0) r.push(`Overdue by ${od} day${od === 1 ? "" : "s"} — push to Live or revise the go-live date.`); }
    if (asset.status === "Edit" && asset.edit_assigned_at) { const d = dDiff(asset.edit_assigned_at, new Date().toISOString()); if (d != null && d >= 7) r.push(`In Edit for ${d} days — check in with the editor.`); }
    if (asset.status === "Live" && !asset.live_url) r.push("Live but no Live URL — add it so performance can be tracked.");
    if (isLive && asset.live_url && Object.keys(asset.engagement_metrics || {}).length === 0) r.push("Live — add performance KPIs (or upload an export).");
    if (["Edit", "Internal Review", "Client Review", "Revisions"].includes(asset.status) && !asset.team?.editor) r.push("No editor assigned — the performance report won't credit anyone.");
    return r;
  })();
  const stageDate = (s) => (asset.stage_history || {})[s];
  const perfHasData = Object.keys(asset.engagement_metrics || {}).length > 0;
  const perfExpanded = kpiEditing || (perfOpen === null ? (perfHasData || isLive) : perfOpen);

  // ─────────── HERO (Phase 1 Production rebuild) ───────────
  // AI thumbnails removed. Only a real uploaded still (hero_frame_url) or a
  // platform poster (live_thumbnail_url) shows; otherwise a clean letter card.
  const isPortrait = ["reel", "short"].includes((asset.asset_type || "").toLowerCase());
  const heroSrc = asset.hero_frame_url || asset.live_thumbnail_url || "";
  const isAdmin = (user?.role || "").toLowerCase() === "admin" || isSuperAdmin(user?.email);
  // Save a go-live date field. planned/revised are write-once for non-admins
  // (the backend enforces it too); actual is always editable.
  const saveGoLive = async (field, value) => {
    try {
      await api.put(`/production/assets/${assetId}`, { [field]: value });
      await loadAsset();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    }
  };

  return (
    <div className="max-w-[1140px] mx-auto space-y-4">
      {/* Header — compact, no media/thumbnail */}
      <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg text-[var(--t2)] hover:bg-white/5 transition-colors flex-shrink-0">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            {editingTitle ? (
              <input
                type="text"
                value={titleDraft}
                autoFocus
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    const t = titleDraft.trim();
                    if (!t) { toast.error("Title required"); return; }
                    try {
                      await api.put(`/production/assets/${assetId}`, { title: t });
                      toast.success("Title saved"); setEditingTitle(false); loadAsset();
                    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                  } else if (e.key === "Escape") { setEditingTitle(false); }
                }}
                onBlur={() => setEditingTitle(false)}
                className="w-full bg-[var(--s2)] border border-[var(--bdr)] rounded px-2 py-1 text-[var(--t1)] text-xl font-bold focus:outline-none"
              />
            ) : (
              <h1
                className="text-[var(--t1)] cursor-text hover:opacity-80 transition-opacity"
                onClick={() => { setTitleDraft(asset.title || ""); setEditingTitle(true); }}
                title="Click to rename"
                style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.02em" }}
              >
                {asset.title}
              </h1>
            )}
            <p className="text-xs mt-1 text-[var(--t3)]">
              {asset.account_name || asset.account_id} · Brief: {asset.brief_title || asset.brief_id}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider bg-[var(--s2)] text-[var(--t2)]">
                {ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}
              </span>
              <span className="text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider text-white"
                    style={{ background: phaseColor }}>
                {asset.status}
              </span>
              {goLiveChip && (
                <span className="text-[10px] px-2.5 py-1 rounded-full font-bold tracking-wide"
                      style={{ color: goLiveChip.color, background: goLiveChip.bg }}>
                  {goLiveChip.label}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={async () => {
              if (!window.confirm("Delete this asset? (Admin only)")) return;
              try {
                await api.delete(`/production/assets/${assetId}`);
                toast.success("Asset deleted");
                navigate("/production");
              } catch (e) { toast.error(e.response?.data?.detail || "Failed — admin only"); }
            }}
            className="text-xs px-3 py-1.5 rounded-lg text-[var(--t2)] hover:bg-red-500/15 hover:text-red-400 transition-colors flex-shrink-0"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Compact header (legacy header replaced by hero above; this wrapper
          intentionally hidden — kept as a stub so the trailing Delete button
          comment block below doesn't end up orphaned. The hero owns all of:
          back, title-edit, account meta, asset_type tag, status pill, delete. */}
      <div style={{ display: 'none' }}>
        <button onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
        <div>
          <div>
            <span>{ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}</span>
          </div>
          <p>{asset.account_name || asset.account_id} · Brief: {asset.brief_title || asset.brief_id}</p>
        </div>
        <button onClick={async () => { if (!window.confirm("Delete this asset? (Admin only)")) return; try { await api.delete(`/production/assets/${assetId}`); toast.success("Asset deleted"); navigate("/production"); } catch (e) { toast.error(e.response?.data?.detail || "Failed — admin only"); } }}
          className="">
          Delete
        </button>
      </div>

      {/* Needs-attention banner — surfaces the most pressing actions up top so
          the user knows what to do next without hunting through the cards. */}
      {risks.length > 0 && (
        <div className="bg-amber-500/[.10] border border-amber-500/30 rounded-xl px-4 py-3 flex items-start gap-2.5">
          <span className="text-amber-600 text-sm mt-0.5 flex-shrink-0">⚠</span>
          <div className="flex flex-col gap-1">
            {risks.slice(0, 3).map((r, i) => (
              <p key={i} className="text-[12px] text-amber-700 leading-snug">{r}</p>
            ))}
          </div>
        </div>
      )}

      {/* Pipeline Progress */}
      <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-4 relative">
        <div className="absolute top-2 right-3">
          <Hint id="asset.pipeline">
            Each stage stamps a timestamp for performance. Don't skip stages — turnaround math (assignment → approval) breaks if you jump.
          </Hint>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {pipeline.map((stage, i) => {
            const isCurrent = stage === asset.status;
            const isPast = i < currentIdx;
            const stagePhase = STATUS_PHASE[stage] || "pre";
            const color = PHASE_COLORS[stagePhase];
            return (
              <div key={stage} className="flex items-center flex-shrink-0">
                <div className="flex flex-col items-center">
                  <button onClick={() => updateStatus(stage)}
                    title={stageDate(stage) ? `Entered ${fmtShort(stageDate(stage))}` : undefined}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                      isCurrent ? "font-bold" : isPast ? "opacity-40" : "opacity-60 hover:opacity-80"
                    }`}
                    style={{
                      background: isCurrent ? color + "20" : "transparent",
                      borderColor: isCurrent ? color + "40" : "#1A1A1A",
                      color: isCurrent ? color : isPast ? "#888" : "#666",
                    }}>
                    {isPast ? "✓ " : ""}{stage}
                  </button>
                  <span className="text-[9px] text-[#888] mt-1 font-mono h-3 leading-none">{stageDate(stage) ? fmtShort(stageDate(stage)) : ""}</span>
                </div>
                {i < pipeline.length - 1 && <ChevronRight size={12} className="text-[#222] mx-0.5 flex-shrink-0 self-start mt-2" />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Left: Details + Team */}
        <div className="col-span-2 space-y-4">
          {/* Description */}
          {asset.description && (
            <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-4">
              <p className="text-xs text-[#888] font-heading tracking-wider mb-2">DESCRIPTION</p>
              <p className="text-[13px] text-[var(--t2)]">{asset.description}</p>
            </div>
          )}

          {/* Versions (Post-Production) */}
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-[#888] font-heading tracking-wider">VERSIONS</p>
              <span className="text-xs text-[#666]">{versions.length} version{versions.length !== 1 ? "s" : ""}</span>
            </div>
            {versions.length === 0 ? (
              <div className="text-center py-6 text-xs text-[#333] border border-dashed border-[var(--bdr)] rounded-lg">
                No versions uploaded yet. Upload an edit/design when ready.
              </div>
            ) : (
              <div className="space-y-2">
                {[...versions].reverse().map((v, i) => (
                  <div key={v.version_id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--s1)] border border-[var(--bdr)]">
                    <span className="text-[12px] font-bold text-[var(--t2)] font-heading w-8">V{v.version_number}</span>
                    <div className="flex-1 min-w-0">
                      {v.url && <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-[12px] text-blue-400 hover:underline flex items-center gap-1 truncate"><ExternalLink size={10} /> {v.url}</a>}
                      {v.notes && <p className="text-xs text-[#888] mt-0.5">{v.notes}</p>}
                    </div>
                    <span className="text-xs text-[#666]">{v.uploaded_by_name} · {new Date(v.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Upload new version */}
            <div className="mt-3 flex gap-2">
              <input value={newVersionUrl} onChange={e => setNewVersionUrl(e.target.value)}
                placeholder="Paste Frame.io / Drive link..."
                className="flex-1 px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[12px] text-white placeholder:text-[#333] focus:outline-none focus:border-blue-500/40" />
              <input value={newVersionNotes} onChange={e => setNewVersionNotes(e.target.value)}
                placeholder="Notes (optional)"
                className="w-[200px] px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[12px] text-white placeholder:text-[#333] focus:outline-none" />
              <button onClick={uploadVersion}
                className="px-4 py-2 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-lg text-xs font-semibold hover:bg-blue-500/20 transition-colors flex items-center gap-1">
                <Upload size={12} /> Upload
              </button>
            </div>
          </div>

          {/* Review Section */}
          {isReviewStatus && (
            <div className="bg-[var(--s1)] border border-purple-500/15 rounded-xl p-4">
              <p className="text-xs text-purple-400 font-heading tracking-wider mb-3">REVIEW — {asset.status.toUpperCase()}</p>
              <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                placeholder="Add feedback, notes, timestamps..."
                className="w-full px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[13px] text-white placeholder:text-[#333] focus:outline-none resize-none mb-3" rows={3} />

              {/* Approve / Request Changes — quality rating moved out of this
                  flow into the post-Live rubric (see /production/assets/.../
                  rubric-rating). Approve no longer needs a craft score, so
                  the team isn't bottlenecked on whoever's qualified to rate. */}
              <div className="flex gap-2">
                <button onClick={() => submitReview("approve")}
                  className="flex-1 py-2.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg text-[12px] font-semibold hover:bg-green-500/20 transition-colors flex items-center justify-center gap-2">
                  <Check size={14} /> Approve
                </button>
                <button onClick={() => submitReview("request_changes")}
                  className="flex-1 py-2.5 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-lg text-[12px] font-semibold hover:bg-amber-500/20 transition-colors flex items-center justify-center gap-2">
                  <RotateCcw size={14} /> Request Changes
                </button>
              </div>
            </div>
          )}

          {/* ═══════ PERFORMANCE — organic + paid KPIs ═══════
              Always visible (we run ads on most assets, so both organic and
              paid matter). Hook / Hold / ER feed the craft objective score.
              Stored in engagement_metrics; later auto-filled from an Ads
              Manager / IG dashboard Excel export. */}
          <div className="bg-[var(--s1)] border border-blue-500/15 rounded-xl p-4">
            <div className="flex items-center justify-between" style={{ marginBottom: perfExpanded ? 12 : 0 }}>
              <p className="text-xs text-blue-400 font-heading tracking-wider inline-flex items-center gap-1.5">
                <span onClick={() => setPerfOpen(!perfExpanded)} className="cursor-pointer select-none inline-flex items-center gap-1.5">
                  <span className="text-[9px]">{perfExpanded ? "▾" : "▸"}</span>PERFORMANCE
                </span>
                <Hint id="asset.kpis">
                  Organic (Instagram) + paid (Meta Ads) KPIs. Hook %, Hold/%Viewed and ER feed the craft objective score. Soon: upload an Ads Manager / IG dashboard export and these auto-fill.
                </Hint>
                {!perfExpanded && <span className="text-[10px] text-[#666] font-normal normal-case tracking-normal">{perfHasData ? "click to expand" : "add when live / running ads"}</span>}
              </p>
              {perfExpanded && <button onClick={() => (kpiEditing ? setKpiEditing(false) : openKpiEdit())} className="text-xs text-blue-400 hover:underline">{kpiEditing ? "Cancel" : "Edit"}</button>}
            </div>
            {perfExpanded && (kpiEditing ? (
              <div className="space-y-3">
                {KPI_GROUPS.map((g) => (
                  <div key={g.key}>
                    <p className="text-[10px] text-[#888] uppercase tracking-wider mb-1.5">{g.label}</p>
                    <div className="grid grid-cols-3 gap-2">
                      {KPI_FIELDS.filter((f) => f.group === g.key).map((f) => (
                        <input key={f.key} value={kpiForm[f.key] ?? ""} title={f.label}
                          onChange={(e) => setKpiForm({ ...kpiForm, [f.key]: e.target.value })}
                          placeholder={f.label}
                          className="px-2 py-1.5 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-[12px] text-white placeholder:text-[#555] focus:outline-none" />
                      ))}
                    </div>
                  </div>
                ))}
                <button onClick={saveKpis} className="w-full py-2 bg-blue-500/15 text-blue-400 border border-blue-500/25 rounded-lg text-xs font-semibold hover:bg-blue-500/25">
                  Save KPIs
                </button>
              </div>
            ) : (() => {
              const em = asset.engagement_metrics || {};
              const hasAny = KPI_FIELDS.some((f) => em[f.key] != null && em[f.key] !== "");
              if (!hasAny) {
                return <p className="text-[12px] text-[#666]">No performance data yet — add it once the asset is live / running ads, or upload an export. <button onClick={openKpiEdit} className="text-blue-400 hover:underline">Add now</button></p>;
              }
              // Only render groups that actually have data — no empty "none" rows.
              return (
                <div className="space-y-3">
                  {KPI_GROUPS.map((g) => {
                    const fields = KPI_FIELDS.filter((f) => f.group === g.key && em[f.key] != null && em[f.key] !== "");
                    if (!fields.length) return null;
                    return (
                      <div key={g.key}>
                        <p className="text-[10px] text-[#888] uppercase tracking-wider mb-1.5">{g.label}</p>
                        <div className="grid grid-cols-3 gap-2">
                          {fields.map((f) => (
                            <div key={f.key} className="bg-[var(--s2)] rounded-lg p-2">
                              <p className="text-[9px] text-[#888] uppercase tracking-wider truncate">{f.label}</p>
                              <p className="text-[15px] font-semibold text-white">{kpiDisplay(f, em[f.key])}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })())}
          </div>

          {/* ═══════ POST-LIVE: RUBRIC RATING (reviewer-only) ═══════
              Visible only when the viewer is on RATING_REVIEWERS, the
              asset is Live, and the 7-day window has opened. Once the
              viewer has submitted, switches to a read-only view of all
              ratings collected so far. */}
          {rubricStatus && asset.status === "Live" && (rubricStatus.eligible_to_rate || rubricStatus.ratings_count > 0) && (
            <div className="bg-[var(--s1)] border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-purple-400 font-heading tracking-wider inline-flex items-center gap-1">
                  CRAFT RATING
                  <Hint id="asset.craft_rating">
                    Rate the work 1–5 on three pillars — Hook (does it stop the scroll), Craft (is it well made), Fit (does it serve the brand/brief). Open to everyone who worked on it, 7 days post-Live. The same scores feed Production Performance and the 6-monthly review.
                  </Hint>
                </p>
                <span className="text-[10px] text-[#666]">
                  {rubricStatus.ratings_count}{rubricStatus.expected_raters ? `/${rubricStatus.expected_raters}` : ""} collaborators rated
                </span>
              </div>

              {rubricStatus.eligible_to_rate ? (
                <div className="space-y-3">
                  <p className="text-[11px] text-[#888] leading-relaxed">
                    Score craft on a 1–5 scale. Engagement numbers above are for context — they don't replace the score.
                  </p>
                  {(rubricStatus.rubric_keys || []).map((dim) => (
                    <div key={dim}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[12px] text-[var(--t2)] capitalize font-medium">{dim}</span>
                        <span className="text-[10px] text-[#555]">1 = below bar · 5 = exceptional</span>
                      </div>
                      <div className="flex gap-1.5">
                        {[1, 2, 3, 4, 5].map((n) => {
                          const active = scoreInputs[dim] === n;
                          return (
                            <button key={n} type="button" onClick={() => setScoreInputs({ ...scoreInputs, [dim]: n })}
                              className={`flex-1 py-2 rounded-lg text-[13px] font-semibold transition-colors border ${
                                active
                                  ? "bg-purple-500/30 text-purple-200 border-purple-400"
                                  : "bg-[var(--s2)] text-[var(--t3)] border-[var(--bdr)] hover:text-[var(--t1)]"
                              }`}>
                              {n}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <textarea value={scoreNotes} onChange={(e) => setScoreNotes(e.target.value)}
                    placeholder="Notes (optional)"
                    className="w-full px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-[13px] text-white placeholder:text-[#444] focus:outline-none resize-none" rows={2} />
                  <button onClick={submitRubricRating} disabled={submittingRating}
                    className="w-full py-2.5 bg-purple-500/15 text-purple-300 border border-purple-500/30 rounded-lg text-xs font-bold hover:bg-purple-500/25 transition-colors disabled:opacity-50">
                    {submittingRating ? "Submitting..." : "Submit rating"}
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-[#666] leading-relaxed">
                  {!rubricStatus.seven_day_open
                    ? `Rating opens 7 days after Live (currently ${asset.live_at ? Math.max(0, 7 - Math.floor((Date.now() - new Date(asset.live_at).getTime()) / 86400000)) : "?"} days to go).`
                    : "Only people who worked on this asset (or a manager) can rate it."}
                </p>
              )}

              {/* Submitted ratings list */}
              {rubricStatus.ratings_count > 0 && (
                <div className="mt-4 pt-3 border-t border-[var(--bdr)] space-y-2">
                  {rubricStatus.ratings.map((r) => (
                    <div key={r.rating_id} className="text-[11px]">
                      <div className="flex items-center justify-between">
                        <span className="text-[var(--t2)] font-medium">{r.rated_by_name}</span>
                        <span className="text-[#666]">{new Date(r.rated_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        {Object.entries(r.scores).map(([k, v]) => (
                          <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/20">
                            {k} {v}/5
                          </span>
                        ))}
                      </div>
                      {r.notes && <p className="text-[#666] mt-1 italic">"{r.notes}"</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Review History */}
          {reviews.length > 0 && (
            <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-4">
              <p className="text-xs text-[#888] font-heading tracking-wider mb-3">REVIEW HISTORY</p>
              <div className="space-y-2">
                {[...reviews].reverse().map(r => (
                  <div key={r.review_id} className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${r.decision === "approve" ? "bg-green-500/[.02] border-green-500/10" : "bg-amber-500/[.02] border-amber-500/10"}`}>
                    <span className={`text-[16px] flex-shrink-0 ${r.decision === "approve" ? "" : ""}`}>{r.decision === "approve" ? "✅" : "↩️"}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] text-white/80 font-medium">{r.reviewer_name}</span>
                        <span className={`text-xs ${r.decision === "approve" ? "text-green-400" : "text-amber-400"}`}>{r.decision === "approve" ? "Approved" : "Requested changes"}</span>
                        {r.version_number && <span className="text-xs text-[#666]">on V{r.version_number}</span>}
                      </div>
                      {r.notes && <p className="text-xs text-[#666] mt-1">{r.notes}</p>}
                    </div>
                    <span className="text-xs text-[#333] flex-shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar: Team + Files + Deadlines */}
        <div className="space-y-4">
          {/* Status */}
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-4">
            <p className="text-xs text-[#888] font-heading tracking-wider mb-2">STATUS</p>
            <span className="text-[14px] font-bold" style={{ color: phaseColor }}>{asset.status}</span>
            {currentIdx < pipeline.length - 1 && (
              <button onClick={() => updateStatus(pipeline[currentIdx + 1])}
                className="mt-2 w-full py-2 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg text-xs font-semibold hover:bg-green-500/20 transition-colors">
                → Advance to {pipeline[currentIdx + 1]}
              </button>
            )}
          </div>

          {/* Editor-missing warning — for assets that are at or past the Edit
              phase, an unassigned editor means this asset's eventual approval
              won't credit anyone in the Performance report. Soft-nudge the
              user to assign one. */}
          {(() => {
            const editorRelevant = ["Edit", "Internal Review", "Client Review", "Revisions"];
            const needsEditor = editorRelevant.includes(asset.status) && !asset.team?.editor;
            if (!needsEditor) return null;
            return (
              <div className="bg-amber-500/15 border border-amber-500/40 rounded-xl p-3 text-xs flex items-start gap-2">
                <span className="text-amber-700 mt-0.5">⚠</span>
                <div>
                  <p className="text-amber-700 font-semibold">No editor assigned</p>
                  <p className="text-amber-700/80 mt-1 leading-relaxed">
                    This asset is in {asset.status}. When it's approved, the editor performance report won't credit anyone. Assign an editor below.
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Details grid — reference cards laid out across the width instead of
          stacked down a narrow right rail. ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          {/* Team */}
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-[#888] font-heading tracking-wider">TEAM</p>
              <button onClick={() => editingTeam ? saveTeam() : setEditingTeam(true)}
                className="text-xs text-blue-400 hover:underline">{editingTeam ? "Save" : "Edit"}</button>
            </div>
            <div className="space-y-2">
              {/* Read mode shows assigned names only; the open roles are
                  summarised in one line so the crew is legible, not a wall of
                  dashes. Edit mode exposes every slot to fill. */}
              {TEAM_ROLES.map(role => {
                const uid = (editingTeam ? teamEdits : asset.team || {})[role];
                const name = (asset.team_names || {})[role];
                if (!editingTeam && !uid) return null;
                return (
                  <div key={role} className="flex items-center justify-between">
                    <span className="text-xs text-[#888] capitalize">{role.replace(/_/g, " ")}</span>
                    {editingTeam ? (
                      <div className="w-[140px]">
                        <UserAutocomplete value={teamEdits[role] || ""} onChange={v => setTeamEdits({ ...teamEdits, [role]: v })}
                          placeholder="Assign..." />
                      </div>
                    ) : (
                      <span className="text-xs text-[var(--t2)]">{name || "—"}</span>
                    )}
                  </div>
                );
              })}
              {!editingTeam && !TEAM_ROLES.some(r => (asset.team || {})[r]) && (
                <p className="text-xs text-[#444]">No one assigned yet — click Edit</p>
              )}
              {!editingTeam && (() => {
                const open = TEAM_ROLES.filter(r => !(asset.team || {})[r]);
                return open.length ? (
                  <p className="text-[10px] text-[#555] pt-1.5 mt-1 border-t border-[var(--bdr)] leading-relaxed capitalize">
                    Open: {open.map(r => r.replace(/_/g, " ")).join(" · ")}
                  </p>
                ) : null;
              })()}
            </div>
          </div>

          {/* Go-Live tracking — planned (mandatory, locked once set) /
              revised (locked once set) / actual (manual, always editable) */}
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-4">
            <p className="text-xs text-[#888] font-heading tracking-wider mb-3">GO-LIVE</p>
            <div className="space-y-3">
              {[
                { key: "planned_go_live", label: "Planned", locked: true },
                { key: "revised_go_live", label: "Revised", locked: true },
                { key: "actual_go_live", label: "Actual", locked: false },
              ].map(({ key, label, locked }) => {
                const val = asset[key] || "";
                const isLockedNow = locked && !!val && !isAdmin; // write-once for non-admins
                return (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <span className="text-xs text-[#888]">{label} go-live{key === "planned_go_live" ? " *" : ""}</span>
                    {isLockedNow ? (
                      <span className="text-xs text-[var(--t2)] flex items-center gap-1.5" title="Locked — admin only">
                        {val} <Clock size={11} className="opacity-50" />
                      </span>
                    ) : (
                      <input
                        type="date"
                        defaultValue={val}
                        onChange={(e) => { if (e.target.value && e.target.value !== val) saveGoLive(key, e.target.value); }}
                        className="text-xs bg-[var(--s2)] border border-[var(--bdr)] rounded-lg px-2 py-1 text-[var(--t1)]"
                        style={{ colorScheme: "dark" }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-[#555] mt-3">Planned &amp; Revised lock once set — only an admin can change them after.</p>
          </div>

          {/* Production cycle — shot-on date + computed durations (edit time,
              shoot→go-live). Durations come from the stage timestamps the
              pipeline already stamps; shot-on is editable. */}
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-4">
            <p className="text-xs text-[#888] font-heading tracking-wider mb-3">CYCLE</p>
            {(() => {
              const dayDiff = (a, b) => { if (!a || !b) return null; const d = Math.round((new Date(b) - new Date(a)) / 86400000); return Number.isNaN(d) ? null : d; };
              const shotOn = asset.shot_on || "";
              const editStart = asset.edit_assigned_at, editEnd = asset.edit_completed_at;
              const inEdit = !!editStart && !editEnd;
              const timeInEdit = editStart ? dayDiff(editStart, editEnd || new Date().toISOString()) : null;
              const liveDate = asset.actual_go_live || asset.live_at;
              const shootAnchor = shotOn || asset.shoot_completed_at;
              const shootToLive = dayDiff(shootAnchor, liveDate);
              const fmtDays = (n) => (n == null ? "—" : `${n} day${n === 1 ? "" : "s"}`);
              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-[#888]">Shot on</span>
                    <input type="date" defaultValue={shotOn}
                      onChange={(e) => { if (e.target.value !== shotOn) saveGoLive("shot_on", e.target.value); }}
                      className="text-xs bg-[var(--s2)] border border-[var(--bdr)] rounded-lg px-2 py-1 text-[var(--t1)]" style={{ colorScheme: "dark" }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#888]">Time in edit</span>
                    <span className="text-xs text-[var(--t2)]">{fmtDays(timeInEdit)}{inEdit && timeInEdit != null ? " · ongoing" : ""}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#888]">Shoot → go-live</span>
                    <span className="text-xs text-[var(--t2)]">{fmtDays(shootToLive)}</span>
                  </div>
                  {!editStart && <p className="text-[10px] text-[#555] mt-1">Edit time tracks once the asset reaches the Edit stage.</p>}
                </div>
              );
            })()}
          </div>

          {/* Files + who/when tracking */}
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-4">
            <p className="text-xs text-[#888] font-heading tracking-wider mb-2">FILES & LINKS</p>
            <div className="space-y-2">
              {/* Live URL — first-class field, separate from `files` so the
                  rubric rating cron can find it cheaply. Editable inline so
                  existing Live assets (which never went through the modal)
                  can have their URL added retroactively. */}
              {/* Live URL is always available on the asset page (paste it any
                  time) — not gated to Approved/Live. Setting it stamps live_at
                  and opens the post-Live rubric window. */}
              {true && (
                <div className="border border-green-500/20 bg-green-500/[.04] rounded-lg p-2 -mx-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-green-400 font-semibold inline-flex items-center gap-1">
                      Live URL
                      <Hint id="asset.live_url">
                        Required when marking Live. Without it, the rubric workflow can't reach the asset and the 7-day post-Live rating window never opens.
                      </Hint>
                    </span>
                    <div className="flex items-center gap-2">
                      {asset.live_url && !editingLiveUrl && (
                        <a href={asset.live_url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-400 hover:underline flex items-center gap-1">
                          <ExternalLink size={9} /> Open
                        </a>
                      )}
                      {!editingLiveUrl && (
                        <button
                          onClick={() => { setLiveUrlEditInput(asset.live_url || ""); setEditingLiveUrl(true); }}
                          className="text-[10px] text-[#888] hover:text-[var(--t1)] underline"
                        >
                          {asset.live_url ? "Edit" : "Add"}
                        </button>
                      )}
                    </div>
                  </div>
                  {editingLiveUrl ? (
                    <div className="mt-2 space-y-1.5">
                      <input
                        type="url"
                        value={liveUrlEditInput}
                        onChange={(e) => setLiveUrlEditInput(e.target.value)}
                        placeholder="https://..."
                        autoFocus
                        className="w-full px-2 py-1.5 bg-[var(--s2)] border border-[var(--bdr)] rounded text-[12px] text-white placeholder:text-[#444] focus:outline-none focus:border-green-500/40"
                      />
                      <div className="flex gap-1.5">
                        <button
                          onClick={async () => {
                            const url = liveUrlEditInput.trim();
                            if (!url) { toast.error("URL required"); return; }
                            try {
                              await api.put(`/production/assets/${assetId}`, { live_url: url });
                              toast.success("Live URL saved");
                              setEditingLiveUrl(false);
                              loadAsset();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed");
                            }
                          }}
                          className="flex-1 py-1 bg-green-500/15 text-green-400 border border-green-500/25 rounded text-[11px] font-semibold hover:bg-green-500/25"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => { setEditingLiveUrl(false); setLiveUrlEditInput(""); }}
                          className="px-3 py-1 border border-[var(--bdr)] text-[#999] rounded text-[11px]"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {!asset.live_url && (
                        <p className="text-xs text-[#666] mt-1">— click Add to set</p>
                      )}
                      {asset.live_url && (
                        <p className="text-[10px] text-[#666] mt-1 truncate" title={asset.live_url}>{asset.live_url}</p>
                      )}
                      {asset.live_at && (
                        <p className="text-[10px] text-[#444] mt-0.5">Live since {new Date(asset.live_at).toLocaleDateString()}</p>
                      )}
                    </>
                  )}
                </div>
              )}
              {Object.entries(asset.files || {}).map(([key, val]) => {
                const log = (asset.file_log || {})[key];
                const isEditing = editingFileKey === key;
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-[#888] capitalize flex-shrink-0">{key.replace(/_/g, " ")}</span>
                      {isEditing ? (
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <input
                            autoFocus
                            value={fileEditInput}
                            onChange={(e) => setFileEditInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveFileLink(key);
                              if (e.key === "Escape") { setEditingFileKey(null); setFileEditInput(""); }
                            }}
                            placeholder="https://..."
                            className="flex-1 min-w-0 bg-[var(--s2)] border border-blue-500/30 rounded px-2 py-1 text-[11px] text-white"
                          />
                          <button onClick={() => saveFileLink(key)} className="px-2 py-1 bg-green-500/15 text-green-400 border border-green-500/25 rounded text-[10px] font-semibold hover:bg-green-500/25">Save</button>
                          <button onClick={() => { setEditingFileKey(null); setFileEditInput(""); }} className="px-2 py-1 border border-[var(--bdr)] text-[#999] rounded text-[10px]">Cancel</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {val ? <a href={val} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline flex items-center gap-1"><ExternalLink size={9} /> Open</a> : <span className="text-xs text-[#333]">—</span>}
                          <button
                            onClick={() => { setEditingFileKey(key); setFileEditInput(val || ""); }}
                            title={val ? "Edit link" : "Add link"}
                            className="text-[#666] hover:text-blue-400 transition-colors"
                          >
                            <Pencil size={10} />
                          </button>
                        </div>
                      )}
                    </div>
                    {log && !isEditing && <p className="text-xs text-[#333] mt-0.5">Added by {log.added_by_name} · {new Date(log.added_at).toLocaleDateString()}</p>}
                  </div>
                );
              })}
              {Object.keys(asset.files || {}).length === 0 && !asset.live_url && <p className="text-xs text-[#333]">No files attached</p>}
            </div>
          </div>

        </div>

      {/* ═══════ SHOOT BOOKING MODAL ═══════ */}
      {showShootBooking && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: "rgba(0,0,0,.7)", backdropFilter: "blur(8px)" }}>
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl w-full max-w-[420px] mx-4 p-6" style={{ boxShadow: "0 24px 80px rgba(0,0,0,.6)" }}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center"><Calendar size={16} className="text-amber-400" /></div>
                <div>
                  <h2 className="text-[15px] font-bold text-white font-heading">Schedule Shoot</h2>
                  <p className="text-xs text-[#666]">{asset.title}</p>
                </div>
              </div>
              <button onClick={() => setShowShootBooking(false)} className="text-[#555] hover:text-red-400 hover:bg-red-500/10 transition-all p-1.5 rounded-lg"><X size={18} /></button>
            </div>

            <div className="space-y-4">
              {/* Studio Select */}
              <div>
                <label className="text-xs text-[#888] block mb-1.5 font-heading tracking-wider">STUDIO / VENUE</label>
                <select value={shootForm.studio_id} onChange={e => setShootForm({ ...shootForm, studio_id: e.target.value })}
                  className="w-full px-3 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[13px] text-white focus:outline-none focus:border-amber-500/40 cursor-pointer">
                  <option value="">Outdoor / Client Site (no studio)</option>
                  {studios.filter(s => s.status === "active").map(s => (
                    <option key={s.studio_id} value={s.studio_id}>{s.name}{s.floor ? ` — Floor ${s.floor}` : ""}</option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="text-xs text-[#888] block mb-1.5 font-heading tracking-wider">SHOOT DATE</label>
                <input type="date" value={shootForm.date} onChange={e => setShootForm({ ...shootForm, date: e.target.value })}
                  className="w-full px-3 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[13px] text-white focus:outline-none focus:border-amber-500/40 cursor-pointer" />
              </div>

              {/* Time Slots — 1–4 contiguous */}
              <div>
                <label className="text-xs text-[#888] block mb-1.5 font-heading tracking-wider">TIME SLOTS <span className="text-[#555] font-normal normal-case tracking-normal">· tap 1–4 (contiguous)</span></label>
                <div className="grid grid-cols-4 gap-2">
                  {SLOT_DEFS.map(slot => {
                    const sel = [...(shootForm.slots || [])];
                    const lo = sel.length ? Math.min(...sel) : -1, hi = sel.length ? Math.max(...sel) : -1;
                    const inRange = slot.k >= lo && slot.k <= hi;
                    return (
                      <button key={slot.k} onClick={() => {
                        let next;
                        if (!sel.length) next = [slot.k];
                        else if (slot.k < lo) next = [slot.k, hi];
                        else if (slot.k > hi) next = [lo, slot.k];
                        else if (slot.k === lo && slot.k === hi) next = [];
                        else if (slot.k === lo) next = [lo + 1, hi];
                        else if (slot.k === hi) next = [lo, hi - 1];
                        else next = [slot.k];
                        setShootForm({ ...shootForm, slots: next });
                      }}
                        className={`py-2.5 rounded-lg border text-center transition-all ${
                          inRange ? "bg-amber-500/15 border-amber-500/40 text-amber-400"
                                  : "border-[var(--bdr)] text-[#666] hover:text-[#999]"
                        }`}>
                        <span className="text-[11px] font-semibold block">{slot.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 mt-6">
              <button onClick={() => setShowShootBooking(false)}
                className="flex-1 py-2.5 border border-[var(--bdr)] text-[#999] rounded-xl text-xs font-semibold hover:text-[var(--t1)] transition-colors">
                Cancel
              </button>
              <button onClick={confirmShootBooking} disabled={bookingSubmitting}
                className="flex-1 py-2.5 bg-amber-500/15 text-amber-400 border border-amber-500/25 rounded-xl text-xs font-bold hover:bg-amber-500/25 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                <Calendar size={14} /> {bookingSubmitting ? "Scheduling..." : "Schedule Shoot"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ LIVE URL MODAL ═══════ */}
      {/* Prompted when an asset is being moved to "Live" without a URL on
          file. Required because the rubric rating workflow needs to know
          where the asset went so engagement can be tied to outcome. */}
      {showLiveUrlModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: "rgba(0,0,0,.7)", backdropFilter: "blur(8px)" }}>
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl w-full max-w-[460px] mx-4 p-6" style={{ boxShadow: "0 24px 80px rgba(0,0,0,.6)" }}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <ExternalLink size={16} className="text-green-400" />
                </div>
                <div>
                  <h2 className="text-[15px] font-bold text-white font-heading">Marking Live</h2>
                  <p className="text-xs text-[#666]">{asset.title}</p>
                </div>
              </div>
              <button onClick={() => { setShowLiveUrlModal(false); setLiveUrlInput(""); }}
                className="text-[#555] hover:text-red-400 hover:bg-red-500/10 transition-all p-1.5 rounded-lg"><X size={18} /></button>
            </div>

            <div>
              <label className="text-xs text-[#888] block mb-1.5 font-heading tracking-wider">LIVE URL *</label>
              <input
                type="url"
                value={liveUrlInput}
                onChange={(e) => setLiveUrlInput(e.target.value)}
                placeholder="https://www.instagram.com/p/..."
                autoFocus
                className="w-full px-3 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[13px] text-white focus:outline-none focus:border-green-500/40"
              />
              <p className="text-[11px] text-[#666] mt-2 leading-relaxed">
                Where did this asset run? The 7-day post-Live rating cycle uses this to tie engagement (shares, saves, ER rate) back to craft scores.
              </p>
            </div>

            <div className="flex gap-2 mt-6">
              <button onClick={() => { setShowLiveUrlModal(false); setLiveUrlInput(""); }}
                className="flex-1 py-2.5 border border-[var(--bdr)] text-[#999] rounded-xl text-xs font-semibold hover:text-[var(--t1)] transition-colors">
                Cancel
              </button>
              <button
                onClick={() => {
                  const url = liveUrlInput.trim();
                  if (!url) { toast.error("Live URL is required"); return; }
                  setShowLiveUrlModal(false);
                  setLiveUrlInput("");
                  updateStatus("Live", { live_url: url });
                }}
                className="flex-1 py-2.5 bg-green-500/15 text-green-400 border border-green-500/25 rounded-xl text-xs font-bold hover:bg-green-500/25 transition-colors flex items-center justify-center gap-2">
                <ExternalLink size={14} /> Mark Live
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
